// Robust auto-update watcher — no manual refresh, no user instructions.
//
// It does NOT trust window.env (the <script src="/config.js"> value can be a
// stale immutable-cached copy). Instead it re-fetches config.js from the network
// with `cache: "no-store"`, which bypasses even an immutable HTTP cache, so it
// always sees the REAL deployed build id. It remembers the last-applied build id
// in localStorage. When the deployed id differs, it clears the stale persisted
// store and reloads into the new build. Works the same for the master->staging
// build model and the staging->prod promote model, because both regenerate
// config.js with a fresh REACT_APP_BUILD_ID. Cross-platform (standard web APIs).

const BUILD_ID_KEY = "coralis-build-id";

const clearPersistedState = () => {
  try {
    const language = window.localStorage.getItem("coralis-language-preference");
    window.sessionStorage.clear();
    window.localStorage.clear();
    if (language) {
      window.localStorage.setItem("coralis-language-preference", language);
    }
  } catch (e) {
    // storage blocked (private mode) — nothing to clear.
  }
};

const fetchDeployedBuildId = async () => {
  const res = await fetch(`/config.js?ts=${new Date().getTime()}`, {
    cache: "no-store",
  });
  if (!res.ok) return null;
  const text = await res.text();
  const match = text.match(/REACT_APP_BUILD_ID:\s*'([^']*)'/);
  return (match && match[1]) || null;
};

let checking = false;

export const checkForUpdate = async () => {
  if (checking) return;
  checking = true;
  try {
    const latest = await fetchDeployedBuildId();
    if (!latest) return; // no build id deployed (e.g. local dev)

    let stored;
    try {
      stored = window.localStorage.getItem(BUILD_ID_KEY);
    } catch (e) {
      return; // storage unavailable — can't compare or clear safely.
    }

    if (stored === null) {
      // First build id we can read — adopt it, don't reload.
      try {
        window.localStorage.setItem(BUILD_ID_KEY, latest);
      } catch (e) {}
      return;
    }

    if (stored !== latest) {
      // A newer build is deployed: clear the stale store and reload into it.
      // Clearing before reload keeps the reloaded build from rehydrating an
      // incompatible snapshot. index.html is no-cache, so reload loads the new
      // bundle without a hard refresh.
      clearPersistedState();
      try {
        window.localStorage.setItem(BUILD_ID_KEY, latest);
      } catch (e) {}
      window.location.reload();
    }
  } catch (e) {
    // offline / blocked — retry on the next trigger.
  } finally {
    checking = false;
  }
};

// Start watching. Checks on load, when the tab regains focus/visibility, and on
// a slow interval. Returns a cleanup function. Set up once at app load.
export const startUpdateWatcher = ({ intervalMs = 5 * 60 * 1000 } = {}) => {
  const onVisible = () => {
    if (document.visibilityState === "visible") checkForUpdate();
  };
  window.addEventListener("focus", checkForUpdate);
  document.addEventListener("visibilitychange", onVisible);
  const intervalId = window.setInterval(checkForUpdate, intervalMs);
  checkForUpdate();
  return () => {
    window.removeEventListener("focus", checkForUpdate);
    document.removeEventListener("visibilitychange", onVisible);
    window.clearInterval(intervalId);
  };
};
